import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useEarningsHistory,
  usePanelist,
  useProfile,
  useTasks,
  useUpdateMemberCheckIn,
} from "../api";
import { SkeletonBlock } from "../components/ui/AsyncState";
import { Banner } from "../components/ui/Banner";
import { CheckInModal } from "../components/ui/CheckInModal";
import { Page } from "../components/ui/Page";
import { usePanelPath } from "../routing";
import { useProfileCompleteness } from "./ProfilePage";
import { TaskReveal } from "./TaskRevealPage";

export function DashboardPage() {
  const navigate = useNavigate();
  const panelPath = usePanelPath();
  const panelistQuery = usePanelist();
  const tasksQuery = useTasks();
  const earningsQuery = useEarningsHistory({ dateRange: "all", status: "all" });
  const profileQuery = useProfile();
  const [showWelcome, setShowWelcome] = useState(true);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkInStep, setCheckInStep] = useState<
    "confirm-membership" | "confirm-withdraw" | "withdraw-complete"
  >("confirm-membership");

  const isLoading =
    panelistQuery.isLoading ||
    tasksQuery.isLoading ||
    earningsQuery.isLoading ||
    profileQuery.isLoading;
  const error =
    panelistQuery.error ??
    tasksQuery.error ??
    earningsQuery.error ??
    profileQuery.error;
  const panelist = panelistQuery.data;
  const completeness = useProfileCompleteness();
  const profile = profileQuery.data;
  const pendingCheckInId = panelist?.pendingCheckIn?.id;
  const updateMemberCheckIn = useUpdateMemberCheckIn(pendingCheckInId);
  const showProfileWarning =
    !!panelist &&
    !!profile &&
    !!completeness &&
    !!completeness.completePct &&
    (panelist.isCheckinDue || completeness.completePct < 100);

  useEffect(() => {
    if (panelist?.isCheckinDue && pendingCheckInId) {
      setCheckInOpen(true);
      setCheckInStep("confirm-membership");
    } else {
      setCheckInOpen(false);
    }
  }, [panelist?.isCheckinDue, pendingCheckInId]);

  function handleReviewProfile() {
    if (!pendingCheckInId) return;
    setCheckInOpen(false);
    navigate(panelPath(`/profile/edit?checkInId=${pendingCheckInId}`));
  }

  function handleConfirmContinue() {
    if (!pendingCheckInId) return;
    updateMemberCheckIn.mutate(
      { response: "continue" },
      {
        onSuccess: () => {
          handleReviewProfile();
        },
      },
    );
  }

  function handleConfirmWithdraw() {
    if (!pendingCheckInId) return;
    updateMemberCheckIn.mutate(
      { response: "withdraw" },
      {
        onSuccess: () => {
          setCheckInStep("withdraw-complete");
        },
      },
    );
  }

  function handleProfileBannerAction() {
    navigate(
      pendingCheckInId
        ? panelPath(`/profile/edit?checkInId=${pendingCheckInId}`)
        : panelPath("/profile/edit"),
    );
  }

  return (
    <Page>
      <CheckInModal
        error={updateMemberCheckIn.error}
        isPending={updateMemberCheckIn.isPending}
        onClose={() => setCheckInOpen(false)}
        onConfirmContinue={handleConfirmContinue}
        onConfirmWithdraw={handleConfirmWithdraw}
        onReviewProfile={handleReviewProfile}
        onStartWithdraw={() => setCheckInStep("confirm-withdraw")}
        open={checkInOpen}
        step={checkInStep}
      />

      {showWelcome && (
        <Banner
          action={
            isLoading || !panelist ? undefined : showProfileWarning ? (
              <button
                className="btn btn-sm"
                onClick={handleProfileBannerAction}
                type="button"
              >
                Update profile
              </button>
            ) : undefined
          }
          onClose={() => setShowWelcome(false)}
          variant={showProfileWarning ? "amber" : "subtle"}
        >
          {isLoading || !panelist ? (
            <SkeletonBlock height={20} width={200} />
          ) : showProfileWarning ? (
            <>
              <strong>Your profile needs a quick update</strong>
              <span>
                Complete your profile to keep your account details current and
                avoid missing tasks.
              </span>
            </>
          ) : (
            <strong>Welcome back, {panelist.firstName}</strong>
          )}
        </Banner>
      )}

      <div style={{ display: "flex", gap: 20, flexDirection: "column" }}>
        <div>
          <TaskReveal loading={isLoading || !panelist} />
        </div>
      </div>
    </Page>
  );
}
